<template>
  <div class="relative text-center flex flex-col items-center w-full">
    <div class="flex justify-between w-full px-1">
      <div class="flex flex-col justify-center">
        <div class="flex items-center mb-2">
          <NPopper hover offset-y="5" placement="top" append-to-body>
            <SymbolTradeStateIcon
              v-gid="{
                module: 'symbol',
                screen: 'home',
                section: 'symbolInfo',
                component: 'symbolTradeStateIcon',
              }"
              class="me-1 cursor-pointer"
              size="medium"
              :trading-state-code="symbol.instrumentTradingState"
              @click="
                openSymbolTradeStateDialog(
                  symbol.instrumentTradingState as number
                )
              "
            />

            <template #content>
              <div
                class="flex justify-center bg-neutral-secondary py-1 rounded-md w-20"
              >
                <NText
                  class="text-neutral-primary"
                  variant="caption2"
                  weight="medium"
                >
                  {{ TRADING_STATE_INFO[symbol.instrumentTradingState].title }}
                </NText>
              </div>
            </template>
          </NPopper>

          <TextHorizontalScroll container-width="38px">
            <Retail :isin="getSymbolIsin(symbol)">
              <NText
                :variant="listItem ? 'caption1' : 'body'"
                weight="bold"
                class="inline-block pr-1 xl:p-0"
              >
                {{ symbol.instrumentName }}
              </NText>
            </Retail>
          </TextHorizontalScroll>

          <div v-if="!isMarketMap" class="flex items-center mr-3">
            <NPopper :hover="true" offset-y="5">
              <NButton
                v-gid="{
                  module: 'symbol',
                  screen: 'home',
                  section: 'symbolDetail',
                  component: 'codalLink',
                }"
                variant="fill"
                size="mini"
                class="w-full font-medium text-o text-neutral-secondary-hover px-1 h-5 !rounded-full flex items-center ml-1"
                color="gray"
                @click="openLink(codalLink)"
              >
                <NegarCodalIcon />
              </NButton>

              <template #content>
                <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
                  <NText
                    weight="medium"
                    variant="caption2"
                    class="text-neutral-quaternary"
                  >
                    {{ t('symbol.codal.link') }}
                  </NText>
                </div>
              </template>
            </NPopper>

            <NPopper :hover="true" offset-y="5">
              <NButton
                v-gid="{
                  module: 'symbol',
                  screen: 'home',
                  section: 'symbolDetail',
                  component: 'tseLink',
                }"
                variant="fill"
                size="mini"
                class="w-full font-medium text-o text-neutral-secondary-hover px-1 h-5 !rounded-full whitespace-nowrap flex items-center"
                color="gray"
                @click="openLink(tseLink)"
              >
                <NegarTseIcon />
              </NButton>

              <template #content>
                <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
                  <NText
                    weight="medium"
                    variant="caption2"
                    class="text-neutral-quaternary"
                  >
                    {{ t('symbol.tehran_exchange.link') }}
                  </NText>
                </div>
              </template>
            </NPopper>
          </div>
        </div>

        <div class="flex items-center">
          <NText
            variant="caption1"
            weight="medium"
            class="text-neutral-secondary-hover"
          >
            {{ t('misc.final') }}
          </NText>

          <NumberFormatter
            #="{ number }"
            :value="(symbol.closingPriceVariationPercent ?? 0) / 100"
            maximum-fraction-digits="2"
            sign-display="always"
            number-style="percent"
          >
            <NText
              variant="overline"
              weight="bold"
              dir="ltr"
              class="mx-1 mt-0.5"
              :class="{
                'text-neutral-secondary':
                  (symbol.closingPriceVariationPercent ?? 0) === 0,
                'text-brand-primary':
                  (symbol.closingPriceVariationPercent ?? 0) > 0,
                'text-critical-primary':
                  (symbol.closingPriceVariationPercent ?? 0) < 0,
              }"
            >
              ({{ number }})
            </NText>
          </NumberFormatter>

          <NumberFormatter #="{ number }" :value="symbol.closingPrice">
            <NText variant="overline" weight="bold" class="mt-0.5">
              {{ number }}
            </NText>
          </NumberFormatter>
        </div>
      </div>

      <div class="w-[70px]">
        <SymbolLastPrice
          :symbol="symbol"
          :price-variant="listItem ? 'body' : 'h2'"
          :show-currency="false"
        />
      </div>
    </div>

    <div
      v-if="VALID_SECTOR_INVESTMENT_FUNDS.includes(symbol.sectorCode ?? '')"
      class="w-full py-0.5"
    >
      <div
        v-gid="{
          module: 'symbol',
          screen: 'home',
          section: 'symbolDetail',
          component: 'bubbleInfoButton',
        }"
        class="flex items-center w-fit bg-blue-secondary rounded-full px-3 cursor-pointer"
        @click="openInstrumentFundsBubbleInfoDialog"
        @mouseover.stop
      >
        <div class="inline-flex items-center relative ml-2">
          <NegarBubbleIcon class="text-blue-primary" />

          <NText
            weight="medium"
            variant="caption2"
            class="text-neutral-secondary"
          >
            {{ t('symbol.bubble.title') }}
          </NText>
        </div>

        <NumberFormatter
          #="{ number }"
          :value="bubblePercent / 100"
          number-style="percent"
          maximum-fraction-digits="2"
          sign-display="always"
        >
          <NText
            weight="medium"
            variant="caption2"
            dir="ltr"
            class="cursor-pointer text-neutral-secondary"
          >
            {{ number }}
          </NText>
        </NumberFormatter>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { NegarTseIcon, NegarCodalIcon, NegarBubbleIcon } from '@negar/icons';
import {
  getSymbolIsin,
  CODAL_LINK,
  TSE_LINK,
  VALID_SECTOR_INVESTMENT_FUNDS,
  calcBubbleOfInvestmentFunds,
} from '@core/symbol';
import type { Instrument } from '@libs/api-reference';

import {
  Retail,
  SymbolLastPrice,
  SymbolTradeStateIcon,
  TRADING_STATE_INFO,
  useSymbolDialogs,
} from '@/modules/symbol';

import NumberFormatter from '@/components/Number.vue';
import TextHorizontalScroll from '@/components/TextHorizontalScroll.vue';

type SymbolInfoCard = {
  symbol: Instrument;
  listItem?: boolean;
  hideChart?: boolean;
  isMarketMap?: boolean;
};

const props = withDefaults(defineProps<SymbolInfoCard>(), {
  listItem: false,
  hideChart: false,
  isMarketMap: false,
});

const { t } = useI18n();

const { openSymbolTradeStateDialog, openInstrumentFundsBubbleInfoDialog } =
  useSymbolDialogs();

const bubblePercent = computed(() =>
  props.symbol.sectorCode &&
  VALID_SECTOR_INVESTMENT_FUNDS.includes(props.symbol.sectorCode)
    ? calcBubbleOfInvestmentFunds({
        lastTradePrice: props.symbol.lastTradePrice,
        priceOfNav: props.symbol.priceOfRedemptionNav,
      })
    : 0
);

const tseLink = computed(() => {
  return `${TSE_LINK}${props.symbol.tmcId}`;
});

const codalLink = computed(() => {
  return `${CODAL_LINK}${props.symbol.instrumentName}`;
});

const openLink = (link: string) => {
  window.open(link, '_blank');
};
</script>
